import React, { Component } from "react";
import cn from "classnames";
import { FormattedMessage } from "react-intl";
import detector from "detector";

import { isMobile, pianoParser, aspectRatio as ar } from "../../../../../common/parameters";
import { IBar, IDoPos, IPitch } from "../../../../../common/types";

import voices from "../../../../voices";
import Icon from "../../../../icon";

import Score from "../../../Score/score-display";
import { parseDo } from "../../../Score/methods/changeDo";
import Modal from "../../../Modal";
import Form, { Title, Submit } from "../../../Form";

import Wrapper from "../../Wrapper";

import getBirdsPath from "../Common/getBirdsPath";
import MessageDisplay from "../Common/MessageDisplay";
import NotEdited from "../Common/NotEdited";
import play, { resetPlay } from "../Common/play";
import setUp from "../Common/setupNotation";
import getKeyOffset from "../Common/getKeyOffset";
import getScale from "../Common/getScale";
import getScore from "../Common/getScore";
import scrollToBar from "../Common/scrollToBar";

import { PlayBtn, Label } from "../common-style";

import Recorder from "./recorder";
import {
    ScoreWrapper,
    Asset1Wrapper,
    Asset2Wrapper,
    Asset3Wrapper,
    RecBtn,
    recBtnDimension,
    recBtnDimensionLow,
    OctaveSelectors,
    OctaveButton,
    OctaveConnector,
    UseSafari,
    EasterBackground,
    ChristmasBackground,
} from "./styles";
import messages from "./messages";
import { getStoreValue, saveStoreValue } from "./store";
import { IProps, IState } from "./interfaces";
import { INotationThis } from "../Common/interfaces";
import { SolfaAudioType } from "../../../../store/user/models";
import { getAccidentals } from "../../../Score/score-display/utils";

const getIsIosUnsupported = () => {
    if (!process.env.WEBPACK) return false;
    if (detector.os.name === "ios" && detector.browser.name !== "safari") {
        return true;
    }
    return false;
};

class Singing extends Component<IProps, IState> {
    static defaultProps = {
        handlePlayCount: () => {},
        handleFailCount: () => {},
        completed: () => {},
    };
    isIosUnsupported: boolean = getIsIosUnsupported();
    play = play.bind(this as unknown as INotationThis);
    setUp = setUp.bind(this as unknown as INotationThis);
    recorder
    isRecordBan = false;
    fadePitch: ReturnType<typeof setTimeout>;
    singNoteTimer: ReturnType<typeof setTimeout>;
    distribution?: number[][];
    scoreRef?: HTMLDivElement;
    pauseOffset?: number;
    constructor(props: IProps) {
        super(props);
        this.state = {
            playing: false,
            completed: false,
            currentNote: null,
            notes: [],
            playingNote: [],
            activeVoice: 0,
            recording: false,
            singPitch: undefined,
            singingOctave: getStoreValue(),
            mediaErrorModal: false,
            enabledFrom: null,
            playable: false,
            doPos: {
                value: 0,
                alt: 0,
            },
        };
        if (this.isIosUnsupported) {
            props.completed("singing", true);
        }

        this.recorder = new Recorder(this.gotRecInput, this.mediaError);

        if (props.setReset) {
            props.setReset(this.resetTask);
        }
        if (props.saveState) {
            props.saveState(this.saveState);
        }
        if (props.restoreState) {
            props.restoreState(this.restoreState);
        }
        if (props.showSolution) {
            props.showSolution(this.showSolution);
        }
    }
    resetTask = () => {
        this.setUp();

        this.setState({
            completed: false,
            activeVoice: 0,
        });
    };
    saveState = () => {
        return {
            currentNote: this.state.currentNote,
            completed: this.state.completed,
            activeVoice: this.state.activeVoice,
        };
    };
    restoreState = (state: IState) => {
        this.setState({
            currentNote: state.currentNote,
            completed: state.completed,
            activeVoice: state.activeVoice,
        });
    };
    showSolution = () => {
        this.setState({
            completed: true,
            currentNote: null,
            activeVoice: 0,
        });
    };
    componentDidMount() {
        this.setUp();
    }

    componentWillUnmount() {
        window.removeEventListener("blur", this.blur);
        clearTimeout(this.fadePitch);
        if (this.recorder.globalStream) {
            const track = this.recorder.globalStream.getTracks()[0]; // if only one media track
            track.stop();
        }
        this.recorder.mediaStreamSource = null;
        if (this.state.playing) {
            this.play();
        }
    }

    blur = () => {
        this.play();
    };

    toggleRecord = (isRecording: boolean) => {
        this.setState({ recording: isRecording });
        this.recorder.toggleLiveInput(isRecording);
    };

    mediaError = () => {
        this.setState({ recording: false });
        this.setState({ mediaErrorModal: true });
    };

    gotRecInput = (_pitch: number) => {
        const { activeVoice, completed, singingOctave, enabledFrom } = this.state;
        const currentNote = this.state.currentNote!;
        if (completed) return;
        if (enabledFrom !== null && enabledFrom > Date.now()) return;
        const { task, plan } = this.props;
        const { isMale, tempo: _tempo } = task.data;
        const tempo = _tempo || 60;
        let octaveShift = 0;
        if (isMale && singingOctave === 1) {
            octaveShift = -12;
        } else if (!isMale && singingOctave === 0) {
            octaveShift = 12;
        }
        const pitch = _pitch + octaveShift;
        this.setState({ singPitch: pitch });
        this.fadePitch = setTimeout(() => {
            this.setState({ singPitch: undefined });
        }, 700);

        const score = getScore(plan, task)!;
        const barsKey = `bars${activeVoice || ""}`;
        let bar = (score[barsKey] as IBar[])[currentNote.bar];
        const note = bar.notes[currentNote.note];
        if (!note) return;
        const noteP = note.p as IPitch;
        const accidentals = getAccidentals(score);
        const doPos = parseDo(accidentals[currentNote.bar]);
        let pianoNote =
            60 +
            pianoParser[noteP.s] +
            (noteP.a || 0) +
            getKeyOffset(doPos.value + 6) +
            doPos.alt +
            ((noteP.o || 0) + (score["octave" + activeVoice] || 0)) * 12 -
            48;
        if (pitch === pianoNote) {
            let lastNote;
            do {
                lastNote = { ...currentNote };
                currentNote.note++;
                if (bar.notes.length === currentNote.note) {
                    currentNote.bar++;
                    bar = score[barsKey][currentNote.bar];
                    currentNote.note = 0;
                }
            } while (
                (bar && bar.notes.length && bar.notes[currentNote.note].r) ||
                score[barsKey][lastNote.bar].notes[lastNote.note].tie
            );
            setTimeout(() => {
                if (score[barsKey][currentNote.bar]) {
                    scrollToBar(this.distribution!, this.scoreRef!, currentNote.bar);
                }
            }, 500);
            if (score[barsKey][currentNote.bar]?.notes?.[currentNote.note]) {
                const enabledFrom = Date.now() + (4 / note.l) * (60 / tempo) * (tempo > 120 ? 1 : 0.75) * 1000;
                this.setState({ currentNote, enabledFrom });
            } else {
                this.setState({ completed: true, recording: false });
                const track = this.recorder.globalStream!.getTracks()[0];
                track.stop();
                this.props.completed(this.props.task.type);
            }
        }
    };

    selectNote = (bar: number, note: number) => {
        const { currentNote } = this.state;
        if (currentNote && bar === currentNote.bar && currentNote.note === note) {
            this.playCurrentNote();
        }
    };

    playCurrentNote = () => {
        let { playing, activeVoice, recording } = this.state;
        const currentNote = this.state.currentNote!;
        if (playing) return;
        const { task, plan } = this.props;
        const score = getScore(plan, task)!;
        const accidentals = getAccidentals(score);
        const doPos = parseDo(accidentals[currentNote.bar]);
        const bar = (score["bars" + (activeVoice || "")] as IBar[])[currentNote.bar];
        const { s, a, o } = bar.notes[currentNote.note].p as IPitch;
        let pianoNote =
            60 +
            pianoParser[s] +
            (a || 0) +
            getKeyOffset(doPos.value + 6) +
            doPos.alt +
            ((o || 0) + (score["octave" + activeVoice] || 0)) * 12;
        if (recording) {
            this.recorder.toggleLiveInput(false);
            clearTimeout(this.singNoteTimer);
            this.singNoteTimer = setTimeout(() => {
                this.recorder.toggleLiveInput(true);
            }, 1000);
        }
        const singingNote = {
            p: {} as IPitch,
            pianoNote,
            isLala: true,
            doPos: {
                i: 0,
                o: 1,
            } as IDoPos,
            clef: score[`clef${activeVoice || ""}`] || "treble",
            type: SolfaAudioType.Lala,
            locale: "en",
            octave: 0,
        };
        let loaded = voices.load(singingNote);
        if (loaded) {
            voices.play(singingNote);
        }
    };

    changeSingingOctave = (singingOctave: IState["singingOctave"]) => {
        saveStoreValue(singingOctave);
        this.setState({ singingOctave });
    };

    setRef = (ref: HTMLDivElement) => {
        this.scoreRef = ref;
    };
    setDistribution = (distribution: number[][]) => {
        this.distribution = distribution;
    };

    render() {
        const {
            playable,
            currentNote,
            playing,
            completed,
            activeVoice,
            recording,
            singPitch,
            singingOctave,
            mediaErrorModal,
            playingNote,
        } = this.state;
        const { task, isLow, isDemo, plan, handleHomeworkNext, isEaster, isChristmas } = this.props;
        const { data } = task;
        const score = getScore(plan, task)!;
        if (!data || !playable)
            return (
                <div>
                    <div>
                        <Wrapper>
                            <NotEdited />
                        </Wrapper>
                    </div>
                </div>
            );
        let scale = getScale(plan, task);
        return (
            <Wrapper isDemo={isDemo}>
                {isLow && (
                    <>
                        <Asset1Wrapper>
                            <img src={getBirdsPath("/singing/asset-01.svg")} alt="decoration" />
                        </Asset1Wrapper>
                        <Asset2Wrapper>
                            <img src={getBirdsPath("/singing/asset-02.svg")} alt="decoration" />
                        </Asset2Wrapper>
                        <Asset3Wrapper>
                            <img src={getBirdsPath("/singing/asset-03.svg")} alt="decoration" />
                        </Asset3Wrapper>
                    </>
                )}
                {isEaster && (
                    <EasterBackground />
                )}
                {isChristmas && (
                    <ChristmasBackground />
                )}
                <ScoreWrapper isLow={!!isLow} isEaster={isEaster || isChristmas}>
                    <Score
                        score={score}
                        singing
                        activeNote={currentNote!}
                        currentNote={currentNote!}
                        activeVoice={activeVoice}
                        playingNote={playingNote}
                        selectNote={this.selectNote}
                        scale={scale * (isLow ? 0.5425 : (isEaster || isChristmas) ? (1076 / ar.width) : 1)}
                        whiteBackground={isLow}
                        playing={playing}
                        singPitch={singPitch}
                        setRef={this.setRef}
                        setDistribution={this.setDistribution}
                        isEaster={isEaster || isChristmas}
                    />
                </ScoreWrapper>
                <PlayBtn
                    className={cn("button", "button--play")}
                    isEaster={isEaster}
                    resetPlay={this.pauseOffset && !playing ? resetPlay.bind(this as unknown as INotationThis) : undefined}
                    onClick={() => {
                        if (isMobile) return;
                        this.play(false, "singing");
                    }}
                    onTouchStart={() => {
                        if (isMobile) this.play(false, "singing");
                    }}
                    isLow={isLow}
                    disabled={recording}
                >
                    {playing ? <Icon i="pause" /> : <Icon i="play" />}
                    <Label>
                        <FormattedMessage {...messages.play} />
                    </Label>
                </PlayBtn>
                {!completed && !this.isIosUnsupported && (
                    <RecBtn isEaster={isEaster} onClick={() => this.toggleRecord(!recording)} disabled={playing} isLow={!!isLow}>
                        {recording ? (
                            <Icon i="stop" />
                        ) : (
                            <svg
                                width={isLow ? recBtnDimensionLow : recBtnDimension}
                                height={isLow ? recBtnDimensionLow : recBtnDimension}
                            >
                                <circle
                                    cx={(isLow ? recBtnDimensionLow : recBtnDimension) / 2}
                                    cy={(isLow ? recBtnDimensionLow : recBtnDimension) / 2}
                                    r={(isLow ? recBtnDimensionLow : recBtnDimension) / 4}
                                />
                            </svg>
                        )}
                    </RecBtn>
                )}
                {!this.isIosUnsupported && (
                    <OctaveSelectors isLow={!!isLow} isEaster={isEaster}>
                        <OctaveButton
                            isLow={!!isLow}
                            className={cn({ Active: singingOctave === 0 })}
                            onClick={() => this.changeSingingOctave(0)}
                        >
                            <Icon i="man" />
                        </OctaveButton>
                        <OctaveButton
                            isLow={!!isLow}
                            className={cn({ Active: singingOctave === 1 })}
                            onClick={() => this.changeSingingOctave(1)}
                        >
                            <Icon i="woman" />
                        </OctaveButton>
                        <OctaveConnector isLow={!!isLow} />
                    </OctaveSelectors>
                )}
                {completed && <MessageDisplay isChristmas={isChristmas} isLow={!!isLow} type="completed" handleHomeworkNext={handleHomeworkNext} />}
                {mediaErrorModal && (
                    <Modal close={() => this.setState({ mediaErrorModal: false })}>
                        <Title>
                            <FormattedMessage {...messages.microphoneErrorTitle} />
                        </Title>
                        <p>
                            <FormattedMessage {...messages.microphoneErrorDescription} />
                        </p>
                        <Form>
                            <Submit onClick={() => this.setState({ mediaErrorModal: false })}>
                                <FormattedMessage {...messages.close} />
                            </Submit>
                        </Form>
                    </Modal>
                )}
                {this.isIosUnsupported && (
                    <UseSafari>
                        <FormattedMessage {...messages.useSafari} />
                    </UseSafari>
                )}
            </Wrapper>
        );
    }
}

export default Singing;
